import { Avatar as MuiAvatar } from '@mui/material'
import { getUserInitials, getAvatarGradient } from '../../lib/userUtils'
import { getApiAssetUrl } from '../../lib/api'
import PropTypes from 'prop-types'

/**
 * Avatar component with profile picture or initials fallback
 * @param {Object} props - Component props
 * @param {Object} props.user - User object with first_name, last_name, profile_pic_url (or creator/influencer shape)
 * @param {string} props.src - Optional override for image URL (when user has no profile_pic_url)
 * @param {string} props.alt - Optional override for alt text
 * @param {string} props.size - Size variant: 'small' | 'medium' | 'large'
 * @param {Object} props.sx - Additional styling
 */
export const Avatar = ({ user, src, alt, size = 'medium', sx = {} }) => {
  const effectiveUser = user || (src ? { profile_pic_url: src } : null)
  const initials = getUserInitials(effectiveUser)
  const gradient = getAvatarGradient(effectiveUser)
  const profilePicUrl = getApiAssetUrl(src || effectiveUser?.profile_pic_url)
  const altText = alt || (effectiveUser ? `${effectiveUser.first_name || ''} ${effectiveUser.last_name || ''}`.trim() || effectiveUser.full_name || effectiveUser.email : 'User')

  const sizeMap = {
    small: 32,
    medium: 40,
    large: 80,
  }

  const avatarSize = sizeMap[size] || sizeMap.medium

  if (profilePicUrl) {
    return (
      <MuiAvatar
        src={profilePicUrl}
        alt={altText}
        sx={{
          width: avatarSize,
          height: avatarSize,
          ...sx,
        }}
      />
    )
  }

  return (
    <MuiAvatar
      sx={{
        width: avatarSize,
        height: avatarSize,
        background: gradient,
        fontWeight: 600,
        fontSize: size === 'small' ? '0.875rem' : size === 'large' ? '2rem' : '1.25rem',
        ...sx,
      }}
      aria-label={effectiveUser ? `Avatar for ${effectiveUser.first_name || effectiveUser.email || 'user'}` : 'User avatar'}
    >
      {initials}
    </MuiAvatar>
  )
}

Avatar.propTypes = {
  user: PropTypes.object,
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  sx: PropTypes.object,
}
